import { PATHNAMES } from 'utils/routes/consts'

import type {
  TBrandComponentKey,
  TBrandTabKey,
  TComponentPermission,
  TComponentsPermissions,
  TComponentsPermissionsByModule,
  TComponentsPermissionsOverride,
  TMenuPermissions,
  TMenuPermissionsByModule,
  TMenuPermissionsOverride,
  TModuleRules,
  TModuleRulesOverride,
  TRouterPermissionsByModule,
  TRulesByModule,
} from './winnin-auth-permissions.types'
import {
  MODULE_COMPONENTS_PERMISSIONS,
  MODULE_MENU_PERMISSIONS,
  MODULE_NAME,
  MODULE_ROUTER_PERMISSIONS,
  MODULE_RULES,
} from '../../winnin-lite/common/winnin-lite.consts'

export const SYSTEM_ROUTES = [
  PATHNAMES.LOGIN,
  PATHNAMES.LOGOUT,
  PATHNAMES.ERROR,
  PATHNAMES.ONBOARDING,
  PATHNAMES.GETTING_STARTED,
  PATHNAMES.PUBLIC_DASHBOARD,
  PATHNAMES.SHARED_POST,
]

export const MENU_KEYS = [
  'home',
  'soa',
  'brands',
  'tags',
  'topics',
  'videos',
  'creators',
  'industry',
  'dashboards',
  'reports',
  'compare',
  'zai',
  'liveEvents',
  'trends',
] as const

export const BRAND_TAB_KEYS = [
  'overview',
  'proprietary-channels',
  'earned',
  'paid',
  'videos',
  'portfolio-tracking',
] as const

export const BRAND_COMPONENT_KEYS = [
  'providersChips',
  'zaiQuestions',
  'topicLinks',
  'videoSelect',
  'videoModalTopic',
  'brandTabNiches',
  'brandTabFormats',
  'brandTabAffinities',
  'brandTabAudience',
  'nichesCard',
  'creatorsCard',
  'providerFilterDropdown',
  'territoryAudienceFilter',
  'creatorLink',
  'creatorSelect',
  'zaiSideChatButton',
  'creatorsMonthFilter',
  'brandCreatorsByTier',
  'brandOwnerVisibilityToggle',
] as const

const MENU_PERMISSIONS_DEFAULT: TMenuPermissions = Object.fromEntries(
  MENU_KEYS.map(key => [key, { hidden: false, disabled: false }])
) as TMenuPermissions

const COMPONENT_PERMISSION_DEFAULT: TComponentPermission = { hidden: false, blocked: false }

const COMPONENTS_PERMISSIONS_DEFAULT = {
  brandPageTabs: Object.fromEntries(BRAND_TAB_KEYS.map(key => [key, { ...COMPONENT_PERMISSION_DEFAULT }])) as Record<
    TBrandTabKey,
    TComponentPermission
  >,
  brandComponents: Object.fromEntries(
    BRAND_COMPONENT_KEYS.map(key => [key, { ...COMPONENT_PERMISSION_DEFAULT }])
  ) as Record<TBrandComponentKey, TComponentPermission>,
} satisfies TComponentsPermissions

const MODULE_RULES_DEFAULT: TModuleRules = {
  restrictUploadDateToCurrentYear: false,
}

const withDimensionDefault = <Key extends string>(
  defaults: Record<Key, TComponentPermission>,
  override: Partial<Record<Key, Partial<TComponentPermission>>> = {}
) =>
  Object.fromEntries(
    Object.entries<TComponentPermission>(defaults).map(([key, value]) => [key, { ...value, ...override[key as Key] }])
  ) as Record<Key, TComponentPermission>

const withComponentsDefault = (override: TComponentsPermissionsOverride = {}): TComponentsPermissions => ({
  brandPageTabs: withDimensionDefault(COMPONENTS_PERMISSIONS_DEFAULT.brandPageTabs, override.brandPageTabs),
  brandComponents: withDimensionDefault(COMPONENTS_PERMISSIONS_DEFAULT.brandComponents, override.brandComponents),
})

const withRulesDefault = (override: TModuleRulesOverride = {}): TModuleRules => ({
  ...MODULE_RULES_DEFAULT,
  ...override,
})

const withMenuDefault = (override: TMenuPermissionsOverride = {}): TMenuPermissions => ({
  ...MENU_PERMISSIONS_DEFAULT,
  ...override,
})

export const ROUTER_PERMISSIONS_BY_MODULE: TRouterPermissionsByModule = {
  [MODULE_NAME]: MODULE_ROUTER_PERMISSIONS,
}

export const COMPONENTS_PERMISSIONS_BY_MODULE: TComponentsPermissionsByModule = {
  [MODULE_NAME]: withComponentsDefault(MODULE_COMPONENTS_PERMISSIONS),
}

export const MENU_PERMISSIONS_BY_MODULE: TMenuPermissionsByModule = {
  [MODULE_NAME]: withMenuDefault(MODULE_MENU_PERMISSIONS),
}

export const RULES_BY_MODULE: TRulesByModule = {
  [MODULE_NAME]: withRulesDefault(MODULE_RULES),
}
